/**
 * Erstellt einen freistehenden Custom-Instruction-Prompt inline aus dem
 * Bulk-Wizard heraus (Spec 2026-08-15 §4.1). tool_code bleibt null; der Name
 * bekommt einen Herkunfts-Praefix gegen die Cross-Tool-Sichtbarkeit von
 * tool_code=null-Templates in anderen Preset-Pickern (akzeptiertes
 * Restrisiko laut Spec, keine Aenderung an der Query-Scope).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { createTemplate, type TextTemplate } from '@/components/v2/tools/text/textGenerationApi'

export class DuplicateTemplateNameError extends Error {}

export interface CreateInlineTemplateInput {
  channelLabel: string
  fieldLabel: string
  name: string
  systemPrompt: string
}

function buildTemplateName(input: CreateInlineTemplateInput): string {
  return `${input.channelLabel} · ${input.fieldLabel}: ${input.name}`
}

export function useCreateInlineTemplate() {
  const qc = useQueryClient()

  return useMutation<TextTemplate, unknown, CreateInlineTemplateInput>({
    mutationFn: async (input) => {
      try {
        return await createTemplate({
          name: buildTemplateName(input),
          tool_code: null,
          system_prompt: input.systemPrompt,
        })
      } catch (err) {
        // apiClient's response interceptor normalizes every error (incl. 409) into a
        // plain ApiError { message, status, code } — it never rejects with an
        // AxiosError, so axios.isAxiosError() would never match here.
        const status = (err as { status?: number } | undefined)?.status
        if (status === 409) {
          const message =
            (err as { message?: string } | undefined)?.message ?? 'Ein Prompt mit diesem Namen existiert bereits.'
          throw new DuplicateTemplateNameError(message)
        }
        throw err
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['text-workspace-templates', 'all'] }),
  })
}
